import { useState } from "react";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

/**
 * Newsletter signup form — drops into Footer + Chronicle post end.
 * Posts to /api/newsletter (stored in our own DB; exportable later).
 *
 * Variants:
 *   variant="inline"  — single-row input + button (footer, dense areas)
 *   variant="block"   — stacked block with heading + description (chronicle end)
 */
export default function NewsletterSignup({
  variant = "inline",
  source = "footer",
  className = "",
}) {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    try {
      await api.post("/newsletter", { email: email.trim(), source });
      setDone(true);
      toast.success("Subscribed. Welcome to the Department of Modern Leisure.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't subscribe. Try again?");
    } finally {
      setSubmitting(false);
    }
  };

  if (variant === "block") {
    return (
      <section
        className={`border-y-2 border-[#1A3626] bg-[#EBE7DD] py-16 md:py-24 ${className}`}
        data-testid="newsletter-block"
      >
        <div className="mx-auto max-w-[760px] px-6 text-center md:px-10">
          <div className="font-display text-[10px] font-black uppercase tracking-[0.42em] text-[#93C065] md:text-xs">
            Department of Modern Leisure
          </div>
          <h2 className="font-display mt-4 text-3xl font-black uppercase leading-[0.95] tracking-tight text-[#1A3626] sm:text-4xl md:text-5xl">
            Receive infrequent dispatches.
          </h2>
          <p className="mt-5 text-base leading-relaxed text-[#1A3626]/85 md:text-lg md:leading-[1.65]">
            New drops, Chronicle posts and the occasional report from inside
            the universe. Sent rarely. Curated by one curator and a dog named
            Mush who reviews the long ones.
          </p>
          {done ? (
            <div className="mt-8 inline-flex items-center gap-3 border-2 border-[#1A3626] bg-[#F9F9F4] px-6 py-4 text-[#1A3626]" data-testid="newsletter-success">
              <CheckCircle2 className="h-5 w-5 text-[#93C065]" strokeWidth={2.4} />
              <span className="font-display text-xs font-black uppercase tracking-[0.28em] md:text-sm">
                Subscribed
              </span>
            </div>
          ) : (
            <form
              onSubmit={onSubmit}
              className="mx-auto mt-8 flex max-w-[480px] flex-col gap-3 sm:flex-row"
              data-testid="newsletter-form-block"
            >
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                className="news-input flex-1"
                data-testid="newsletter-input-block"
              />
              <button
                type="submit"
                disabled={submitting}
                className="font-display inline-flex items-center justify-center gap-2 border-2 border-[#1A3626] bg-[#1A3626] px-6 py-3 text-xs font-black uppercase tracking-[0.32em] text-[#F9F9F4] transition-all hover:-translate-y-0.5 hover:bg-transparent hover:text-[#1A3626] disabled:opacity-50"
                data-testid="newsletter-submit-block"
              >
                {submitting ? "Sending\u2026" : "Subscribe"}
                <ArrowRight className="h-4 w-4" strokeWidth={2.5} />
              </button>
            </form>
          )}
        </div>
        <NewsletterStyles />
      </section>
    );
  }

  // Inline variant for footer
  return (
    <div className={className} data-testid="newsletter-inline">
      <div className="font-display text-[10px] font-black uppercase tracking-[0.3em] text-[#EBE7DD]/80 md:text-xs">
        Infrequent dispatches
      </div>
      <p className="mt-2 text-sm leading-relaxed text-[#EBE7DD]/80">
        New drops, Chronicle posts and field reports from inside the universe.
      </p>
      {done ? (
        <div className="mt-3 inline-flex items-center gap-2 border-2 border-[#EBE7DD] bg-transparent px-4 py-2 text-[#EBE7DD]">
          <CheckCircle2 className="h-4 w-4 text-[#93C065]" strokeWidth={2.5} />
          <span className="font-display text-[10px] font-black uppercase tracking-[0.28em]">
            Subscribed
          </span>
        </div>
      ) : (
        <form
          onSubmit={onSubmit}
          className="mt-3 flex flex-col gap-2 sm:flex-row"
          data-testid="newsletter-form-inline"
        >
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="your@email.com"
            className="news-input-dark flex-1"
            data-testid="newsletter-input-inline"
          />
          <button
            type="submit"
            disabled={submitting}
            className="font-display inline-flex items-center justify-center gap-2 border-2 border-[#EBE7DD] px-4 py-2 text-[10px] font-black uppercase tracking-[0.32em] text-[#EBE7DD] transition-all hover:bg-[#EBE7DD] hover:text-[#1A3626] disabled:opacity-50"
            data-testid="newsletter-submit-inline"
          >
            {submitting ? "\u2026" : "Subscribe"}
          </button>
        </form>
      )}
      <NewsletterStyles />
    </div>
  );
}

function NewsletterStyles() {
  return (
    <style>{`
      .news-input{
        background:#F9F9F4;border:2px solid #1A3626;
        padding:12px 14px;font-size:15px;color:#1A3626;
        font-family:inherit;outline:none;
        transition:box-shadow 150ms ease, transform 150ms ease;
      }
      .news-input:focus{ box-shadow: 4px 4px 0 0 #93C065; transform: translate(-1px,-1px); }
      .news-input-dark{
        background:transparent;border:2px solid #EBE7DD;
        padding:8px 12px;font-size:13px;color:#EBE7DD;
        font-family:inherit;outline:none;
      }
      .news-input-dark::placeholder{ color: rgba(235,231,221,0.5); }
      .news-input-dark:focus{ background: rgba(235,231,221,0.06); }
    `}</style>
  );
}
